import { Banner } from '@fullstory/components';
import { Box, Divider, Flex, Text } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { DQMRow } from '@fullstory/proto/fs/services/dx-objects';
import { TrendFrequency } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { shortNumber } from '@fullstory/util/fmt';
import React from 'react';
import { ActivityMonitoringData } from '../../types';
import { ActivityMonitoringTrendsChart } from '../ActivityTabTrendsChart';
import css from './index.module.scss';

interface ObjectDetailsContentProps {
  description: string;
  showDataDirect: boolean;
  eventSupport: DQMRow.EventSupport;
  activityMonitoring: ActivityMonitoringData | null;
  extraContent?: React.ReactNode;
}

export const ObjectDetailsContent: React.FC<ObjectDetailsContentProps> = ({
  eventSupport,
  description,
  extraContent,
  activityMonitoring,
  showDataDirect,
}) => (
  <Flex data-component="ObjectDetailsContent" column alignItems="stretch">
    {description && (
      <>
        <Text appearance="bodySmall" color="emphasized">
          {intl.formatMessage({ defaultMessage: 'Description', id: 'Q8Qw5B' })}
        </Text>
        <Box marginTop={8}>
          <Text appearance="bodySmall">{description}</Text>
        </Box>
        <Divider className={css.divider} />
      </>
    )}
    {activityMonitoring && (
      <Flex column alignItems="stretch">
        <Box marginBottom={8}>
          <Flex justifyContent="space-between">
            <Text appearance="bodySmall" color="emphasized">
              {showDataDirect
                ? intl.formatMessage({ defaultMessage: '7-day event volume', id: 'jiV7+h' })
                : intl.formatMessage({ defaultMessage: '30-day event volume', id: 'oTzA6+' })}
            </Text>
            <Text appearance="bodyMediumBold" color="emphasized">
              {shortNumber(activityMonitoring.totalEventCount)}
            </Text>
          </Flex>
        </Box>
        <Box marginBottom={8}>
          {eventSupport === undefined ||
          eventSupport === DQMRow.EventSupport.EVENT_SUPPORT_NOT_APPLICABLE ||
          eventSupport === DQMRow.EventSupport.EVENT_SUPPORT_SUPPORTED ? (
            <ActivityMonitoringTrendsChart
              sparkline
              volume={activityMonitoring.volume}
              frequency={TrendFrequency.TREND_FREQUENCY_DAILY}
              height={60}
            />
          ) : (
            <Banner
              title={intl.formatMessage({ defaultMessage: 'No action needed', id: '0m+iph' })}
              description={intl.formatMessage({
                defaultMessage:
                  'Data quality monitoring does not support this event type. Normally a chart would appear here.',
                id: 'F25QyP',
              })}
            />
          )}
        </Box>
      </Flex>
    )}
    <>
      <Flex justifyContent="space-between">
        <Text appearance="bodySmall" color="emphasized">
          {activityMonitoring?.firstSeenLabel ??
            intl.formatMessage({ defaultMessage: 'First seen', id: 'tJZ3DG' })}
        </Text>
        <Text appearance="bodySmall">
          {activityMonitoring?.firstSeen
            ? intl.formatDate(activityMonitoring.firstSeen, {
                dateStyle: 'medium',
              })
            : intl.formatMessage({ defaultMessage: 'N/A', id: 'PW+sL4' })}
        </Text>
      </Flex>
      <Divider className={css.divider} />
      <Flex justifyContent="space-between">
        <Text appearance="bodySmall" color="emphasized">
          {intl.formatMessage({ defaultMessage: 'Last seen', id: 'PO83gc' })}
        </Text>
        <Text appearance="bodySmall">
          {activityMonitoring?.lastSeen
            ? intl.formatDate(activityMonitoring.lastSeen, {
                dateStyle: 'medium',
              })
            : intl.formatMessage({ defaultMessage: 'N/A', id: 'PW+sL4' })}
        </Text>
      </Flex>
      <Divider className={css.divider} />
      {extraContent}
    </>
  </Flex>
);
